import { useMutation, useQueryClient } from '@tanstack/react-query'

import { useOsApi } from '@/frontend/hooks/api/use-os-api'

interface ChargeInvoiceParams {
  invoiceId: string
  paymentMethodId: string
}

/**
 * Enriched payment error with the customer-facing hosted invoice URL
 * for QR code self-checkout. The backend only returns the admin-facing
 * Stripe dashboard link — we fetch the hosted URL on the frontend so
 * the charge endpoint stays simple.
 */
export class ChargeFailedError extends Error {
  readonly invoiceUrl: string
  readonly hostedInvoiceUrl: string

  constructor(props: { invoiceUrl: string; hostedInvoiceUrl: string; message?: string }) {
    super(props.message ?? 'Payment failed')
    this.invoiceUrl = props.invoiceUrl
    this.hostedInvoiceUrl = props.hostedInvoiceUrl
  }
}

/** Charges an existing finalized Stripe invoice with the specified payment method. */
export function useChargeInvoice() {
  const api = useOsApi()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ invoiceId, paymentMethodId }: ChargeInvoiceParams) => {
      const response = await api.post('/invoices/:invoiceId/charge', {
        params: { invoiceId },
        body: { paymentMethodId },
      })

      if (!response.success) {
        const { error } = response
        if (error.name === 'FailedPaymentError') {
          // Best-effort fetch of the customer-facing hosted invoice URL for QR code checkout
          let hostedInvoiceUrl = ''
          const urlResponse = await api.get('/invoices/:invoiceId/url', { params: { invoiceId } })
          if (urlResponse.success) {
            hostedInvoiceUrl = urlResponse.data.url
          }

          throw new ChargeFailedError({
            message: error.message,
            invoiceUrl: error.invoiceUrl,
            hostedInvoiceUrl,
          })
        }
        throw new Error(error.message)
      }
      return response.data
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      if (data.orders) {
        for (const id of new Set(data.orders.map((o) => o.patientId))) {
          queryClient.invalidateQueries({ queryKey: ['patient-orders', id] })
          queryClient.invalidateQueries({ queryKey: ['patient-treatments', id] })
          queryClient.invalidateQueries({ queryKey: ['patient-timeline', id] })
        }
        for (const id of new Set(
          data.orders.flatMap((o) => (o.consultationId ? [o.consultationId] : []))
        )) {
          queryClient.invalidateQueries({ queryKey: ['consultation', id] })
        }
      }
    },
  })
}
